// src/utils/bibliographyApi.ts
// 書誌管理（バーコード追加・検索追加）用 API クライアント
// モバイル版 lib/services/books_api.dart の registerBookByIsbn / bulkRegister /
// checkOwnership と同じ契約を Web 向けに移植したもの。

import axios from 'axios';
import { NEW_API, buildIsbnFetchUrl } from '../config/api';

function authHeaders(idToken: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

/** GET /isbn/{isbn} — ISBN検索（認証不要） */
export async function searchByIsbn(isbn: string): Promise<any | null> {
  try {
    const res = await axios.get(buildIsbnFetchUrl(isbn));
    return res.data ?? null;
  } catch (err) {
    return null;
  }
}

/** ISBN検索結果から rl_book_id を抽出（ndl > hanmoto > ol の優先順） */
export function extractRlBookIdFromIsbnResult(isbnResult: any): number | null {
  if (!isbnResult) return null;
  for (const source of ['ndl', 'hanmoto', 'ol']) {
    const sourceData = isbnResult[source];
    if (sourceData && typeof sourceData === 'object') {
      const values = Object.values(sourceData) as any[];
      if (values.length > 0 && values[0]?.rl_book_id != null) {
        return values[0].rl_book_id as number;
      }
    }
  }
  return null;
}

export interface OwnershipResult {
  rl_book_id: number;
  owned_count: number;
  pending_count: number;
  total: number;
}

/** POST /books operation_type=check_ownership */
export async function checkOwnership(rlBookIds: number[], idToken: string): Promise<OwnershipResult[]> {
  const res = await axios.post(
    NEW_API.BOOKS,
    { operation_type: 'check_ownership', rl_book_ids: rlBookIds },
    { headers: authHeaders(idToken) },
  );
  return res.data?.data?.items ?? [];
}

export interface BulkRegisterItem {
  rl_book_id: number;
  shelf_id?: string | null;
}

/** POST /books operation_type=bulk_register（複数冊まとめて登録、SQS経由の非同期処理） */
export async function bulkRegisterItems(
  items: BulkRegisterItem[],
  idToken: string,
  allowDuplicate = false,
): Promise<void> {
  await axios.post(
    NEW_API.BOOKS,
    { operation_type: 'bulk_register', items, allow_duplicate: allowDuplicate },
    { headers: authHeaders(idToken) },
  );
}

/** ISBNから rl_book_id を解決して1冊登録する（バーコードスキャン用） */
export async function registerBookByIsbn(
  isbn: string,
  shelfId: string | null,
  idToken: string,
  allowDuplicate = false,
): Promise<{ rlBookId: number; title?: string }> {
  const isbnResult = await searchByIsbn(isbn);
  if (!isbnResult) {
    throw new Error('ISBNに該当する書籍が見つかりません');
  }
  const rlBookId = extractRlBookIdFromIsbnResult(isbnResult);
  if (rlBookId == null) {
    throw new Error('書誌データにrl_book_idが見つかりません');
  }
  await bulkRegisterItems([{ rl_book_id: rlBookId, shelf_id: shelfId }], idToken, allowDuplicate);
  return { rlBookId, title: isbnResult?.display?.title };
}

/** rl_book_id を指定して1冊登録する（検索結果からの登録用） */
export async function registerBookByRlBookId(
  rlBookId: number,
  shelfId: string | null,
  idToken: string,
  allowDuplicate = true,
): Promise<void> {
  await bulkRegisterItems([{ rl_book_id: rlBookId, shelf_id: shelfId }], idToken, allowDuplicate);
}

export interface BulkStatus {
  has_pending: boolean;
  pending: {
    register: { count: number };
    move: { count: number };
    withdraw: { count: number };
  };
  last_completed: {
    completed_at: number;
    results: Record<string, { succeeded: number; failed: number }>;
  } | null;
}

/** GET /books?bulk_status=true — バルク処理（登録/移動/除籍）の進捗確認 */
export async function getBulkStatus(idToken: string): Promise<BulkStatus> {
  const res = await axios.get(NEW_API.BOOKS, {
    headers: authHeaders(idToken),
    params: { bulk_status: 'true' },
  });
  return res.data;
}

export interface WithdrawnBook {
  global_book_id: string;
  rl_book_id: number;
  global_book_withdrawn_at: string;
  global_book_withdraw_reason: string | null;
  title?: string;
  author?: string;
  publisher?: string;
  pub_year?: number;
  isbn13?: string;
  cover_url?: string;
  volume?: string | number | null;
}

/** GET /books?sort=withdrawn — 除籍済みの本を除籍日が新しい順に取得（除籍済みの本管理画面向け） */
export async function getWithdrawnBooks(
  idToken: string,
  cursor?: string | null,
  limit = 50,
): Promise<{ items: WithdrawnBook[]; nextCursor: string | null }> {
  const params: Record<string, string> = { sort: 'withdrawn', limit: String(limit) };
  if (cursor) params.cursor = cursor;
  const res = await axios.get(NEW_API.BOOKS, { headers: authHeaders(idToken), params });
  const data = res.data?.data ?? {};
  return { items: data.items ?? [], nextCursor: data.next_cursor ?? null };
}

/** POST /books operation_type=restore_withdrawn_book — 除籍を取り消す */
export async function restoreWithdrawnBook(globalBookId: string, idToken: string): Promise<void> {
  await axios.post(
    NEW_API.BOOKS,
    { operation_type: 'restore_withdrawn_book', global_book_id: globalBookId },
    { headers: authHeaders(idToken) },
  );
}

/** POST /books operation_type=delete_book_permanently — 本を完全に削除する（取り消し不可） */
export async function deleteBookPermanently(globalBookId: string, idToken: string): Promise<void> {
  await axios.post(
    NEW_API.BOOKS,
    { operation_type: 'delete_book_permanently', global_book_id: globalBookId },
    { headers: authHeaders(idToken) },
  );
}
